import React, { useState, useEffect, useCallback } from 'react';
import '../App.css';
import { sanitizePlayerName } from '../utils/sanitizer';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import socket, { setGameContext, clearGameContext } from '../utils/socket';
import { openExternalUrl } from '../utils/openExternal';

const LobbyPage = ({ gameId, playerName: initialPlayerName, gameType, onStartGame, onLeaveLobby, initialMaxPlayers, onUpdatePlayerName }) => {
  const { t } = useLanguage();
  
  // Rules modal state
  const [showRulesModal, setShowRulesModal] = useState(false);
  
  // Initialize player name state first
  const [currentPlayerName, setCurrentPlayerName] = useState(initialPlayerName || '');
  const [nameInput, setNameInput] = useState(initialPlayerName || '');
  const [nameError, setNameError] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  // Then initialize other states
  const [players, setPlayers] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [, setConnecting] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState(initialMaxPlayers || 4);
  const [showError, setShowError] = useState(false);
  const [hasAttemptedJoin, setHasAttemptedJoin] = useState(false);

  // Turn timer settings (host only)
  const [turnTimerEnabled, setTurnTimerEnabled] = useState(false);
  const [turnTimerDuration, setTurnTimerDuration] = useState(30);

  // Opponent scores visibility setting (host only)
  const [showOpponentScores, setShowOpponentScores] = useState(false);

  // Functions to handle rules modal
  const openRulesModal = () => {
    setShowRulesModal(true);
  };
  
  const closeRulesModal = () => {
    setShowRulesModal(false);
  };

  // Effect to handle player name prompt
  useEffect(() => {
    // Check if player name is missing or empty
    if (!initialPlayerName || initialPlayerName.trim() === '' || initialPlayerName === '[object Object]') {
      // Try to get a stored name from localStorage
      const storedName = localStorage.getItem('playerName');
      if (storedName && storedName.trim() !== '' && storedName !== '[object Object]') {
        // Use the stored name without showing prompt
        setCurrentPlayerName(storedName);
        setNameInput(storedName);
        // Update parent component with the stored name
        if (onUpdatePlayerName) {
          onUpdatePlayerName(storedName);
        }
      } else {
        // Only show prompt if no valid name is found anywhere
        setShowNamePrompt(true);
      }
    } else {
      // Convert to string to be safe
      const validName = String(initialPlayerName);
      // Save this name to localStorage for future use
      localStorage.setItem('playerName', validName);
      setCurrentPlayerName(validName);
    }
  }, [initialPlayerName, onUpdatePlayerName]);

  // Reset error message after 5 seconds
  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle player name validation
  const validatePlayerName = (name) => {
    const sanitized = sanitizePlayerName(name);
    if (!sanitized || sanitized.trim() === '') {
      setNameError(t('lobby.errors.invalidName'));
      return false;
    }
    
    if (sanitized.length < 1) {
      setNameError(t('lobby.errors.nameTooShort'));
      return false;
    }
    
    if (sanitized.length > 12) {
      setNameError(t('lobby.errors.nameTooLong'));
      return false;
    }
    
    // Name is valid
    setNameError('');
    return true;
  };

  // Handle name change submission
  const handleSubmitName = () => {
    const isValid = validatePlayerName(nameInput);
    if (!isValid) return;
    
    const sanitized = sanitizePlayerName(nameInput);
    
    // Update local state
    setCurrentPlayerName(sanitized);
    
    // Update parent component
    if (onUpdatePlayerName) {
      onUpdatePlayerName(sanitized);
    }
    
    // Hide the prompt
    setShowNamePrompt(false);
    
    // Store the name
    localStorage.setItem('playerName', sanitized);
    
    // Try to update the name on the server
    if (socket && socket.connected) {
      socket.emit('updatePlayerName', { gameId, oldName: initialPlayerName, newName: sanitized });
    }
  };

  // Effect to set up socket handlers
  useEffect(() => {
    // Don't connect if we don't have a player name yet
    if (showNamePrompt) {
      console.log('Waiting for player name before connecting...');
      return;
    }
    
    // Use the currentPlayerName from state which should be valid at this point
    if (!currentPlayerName || currentPlayerName.trim() === '') {
      console.log('No valid player name available, delaying socket connection');
      return;
    }
    
    console.log('Initializing connection to game lobby:', gameId, 'as player:', currentPlayerName);
    setConnecting(true);
      
    // Define handleGameUpdate for socket events
    const handleGameUpdate = (data) => {
      console.log('🔄 Received gameUpdate event:', data);
      if (data && data.players) {
        console.log('🧑‍🤝‍🧑 Setting players from gameUpdate data:', data.players);
        setPlayers(data.players);
      }
      if (data && data.maxPlayers !== undefined) {
        console.log('🔢 Setting maxPlayers to:', data.maxPlayers);
        setMaxPlayers(data.maxPlayers);
      }
      // Sync timer settings from host
      if (data && data.turnTimerEnabled !== undefined) {
        setTurnTimerEnabled(data.turnTimerEnabled);
      }
      if (data && data.turnTimerDuration !== undefined) {
        setTurnTimerDuration(data.turnTimerDuration);
      }
      // Sync opponent scores visibility setting from host
      if (data && data.showOpponentScores !== undefined) {
        setShowOpponentScores(data.showOpponentScores);
      }
    };

    // Only set up listeners if socket instance exists
    if (socket) {
      // Set up event handlers when we connect
      const setupSocketHandlers = () => {
        console.log('🔌 Lobby socket connected! Socket ID:', socket.id);
        setConnectionError(false);
        setConnecting(false);
        setError(null);
        
        // Only join if we haven't already attempted to join
        if (!hasAttemptedJoin) {
          console.log('Using existing game data', { gameId, playerName: currentPlayerName, gameType, maxPlayers: initialMaxPlayers });

          // Join the game room for socket.io with maxPlayers preference
          socket.safeEmit('joinGameSession', {
            gameId,
            playerName: String(currentPlayerName), // Ensure playerName is a string
            maxPlayers: initialMaxPlayers // Pass initial max players preference to server
          }, (err, response) => {
            if (err) {
              console.error('Failed to join game session:', err.message);
              setError('Failed to join game: ' + err.message);
              setShowError(true);
              return;
            }

            if (response && response.success) {
              console.log('Successfully joined game session with players:', response.players);
              setHasAttemptedJoin(true);

              // Set game context for automatic reconnection
              setGameContext(gameId, String(currentPlayerName), 'lobby');

              // Update players list
              if (response.players) {
                setPlayers(response.players);
              }
            } else {
              console.error('Server rejected game join:', response?.error || 'Unknown error');
              const errorMsg = typeof response?.error === 'object'
                ? (response.error?.message || JSON.stringify(response.error) || 'Unknown error')
                : String(response?.error || 'Unknown error');

              // If the server doesn't know this game (typically a refresh against
              // a stale qwixx_game_context), clean up and route the user home
              // instead of leaving them stranded on a broken lobby.
              if (errorMsg.includes('Game not found')) {
                console.warn('joinGameSession rejected: game not found, returning to home');
                clearGameContext();
                if (typeof onLeaveLobby === 'function') onLeaveLobby();
                return;
              }

              // Map server error to translated error message
              let errorMessage = errorMsg;
              if (errorMsg.includes('Name already taken')) {
                errorMessage = t('errors.nameTaken');
              }

              setError('Failed to join game: ' + errorMessage);
              setShowError(true);
            }
          });
        }
      };
      
      // If already connected, set up handlers immediately
      if (socket.connected) {
        setupSocketHandlers();
      }
      
      // Set up socket event listeners
      socket.on('connect', setupSocketHandlers);
      
      socket.on('connect_error', (err) => {
        console.error(`Lobby socket connection error:`, err);
        setConnectionError(true);
        setConnecting(false);
        setError('Server connection error: ' + (err.message || 'Unknown error'));
        setShowError(true);
      });
      
      socket.on('disconnect', (reason) => {
        console.log(`Socket disconnected: ${reason}`);

        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
          // Intentional disconnect, don't show error
          console.log('Clean disconnect, not showing error');
        } else {
          // Unintentional disconnect (VPN change, network issue, etc.)
          setConnectionError(true);
          setError(t('lobby.connectionLost') || 'Connection lost. Attempting to reconnect...');
          setShowError(true);

          // The socket.js auto-rejoin will handle reconnection automatically
          console.log('🔄 Socket will auto-rejoin lobby when connection is restored');
        }
      });
      
      // Game-specific events
      socket.on('gameUpdate', handleGameUpdate);
      
      socket.on('playerJoined', (data) => {
        console.log('👋 Player joined event:', data);
        if (data.players) {
          setPlayers(data.players);
        }
      });
      
      socket.on('playerLeft', (data) => {
        console.log('👋 Player left event:', data);
        if (data.players) {
          setPlayers(data.players);
        }
      });
      
      socket.on('playerReady', (data) => {
        console.log('✅ Player ready event:', data);
        if (data.players) {
          setPlayers(data.players);
        }
      });
      
      socket.on('gameStart', (data) => {
        console.log('🎮 Game started event received:', data);

        // Make sure we have the data needed to start the game
        if (!data) {
          console.error('Received empty gameStart event data');
          setError('Failed to start game: Invalid game data');
          setShowError(true);
          return;
        }

        // Format the data to match what the game expects
        const gameData = {
          gameId: data.gameId || gameId,
          players: data.players || players,
          diceValues: data.diceValues || [],
          currentPlayer: data.currentPlayer,
          maxPlayers: data.maxPlayers || maxPlayers,
          turnTimerEnabled: data.turnTimerEnabled || false,
          turnTimerDuration: data.turnTimerDuration || 30,
          showOpponentScores: data.showOpponentScores || false
        };

        console.log('Starting game with formatted data:', gameData);

        // Update game context to 'game' screen (will be set properly by GamePage)
        // We clear it here so GamePage can set it fresh
        clearGameContext();

        // Forward to the game start handler with the game data
        if (onStartGame) {
          onStartGame(gameData);
        } else {
          console.error('onStartGame callback is not defined');
          setError('Failed to start game: Internal error');
          setShowError(true);
        }
      });
      
      // Effect to set up socket handlers
      const handleError = (error) => {
        console.error('Socket error:', error);
        let errorMessage = typeof error === 'string' ? error : error?.message || error?.error || 'Unknown error';
        
        // Map server error to translated error message
        if (errorMessage.includes('Name already taken') || errorMessage.includes('Name already taken in this game')) {
          errorMessage = t('errors.nameTaken');
        }
        
        setError(errorMessage);
        setShowError(true);
      };
      
      // Listen for errors
      socket.on('error', handleError);
    }
    
    // Clean up event listeners when the component unmounts
    return () => {
      if (socket) {
        console.log('🧹 Cleaning up socket event listeners');
        socket.off('connect');
        socket.off('disconnect');
        socket.off('gameJoined');
        socket.off('playerJoined');
        socket.off('playerLeft');
        socket.off('playerReady');
        socket.off('gameStart');
        socket.off('error');
        socket.off('playerStatusUpdated');
        socket.off('gameState');
        socket.off('reconnect');
        socket.off('reconnect_attempt');
        socket.off('reconnect_error');
        socket.off('reconnect_failed');
        socket.off('connect_error');
        socket.off('connect_timeout');
        socket.off('error');

        // Clean up the gameUpdate handler
        console.log('🔄 Removing gameUpdate handler');
        socket.off('gameUpdate', handleGameUpdate);
      }
      // Note: Don't clear game context on unmount - needed for reconnection after app backgrounding
    };
  }, [gameId, currentPlayerName, gameType, initialMaxPlayers, showNamePrompt, hasAttemptedJoin, onStartGame, maxPlayers, players, t]);

  // Broadcast timer setting changes to other players (host only)
  useEffect(() => {
    if (!socket || !socket.connected || !gameId) return;

    // Only the host should broadcast timer changes
    const player = players.find(p => p.name === currentPlayerName);
    if (!player || !player.isHost) return;

    socket.emit('updateTimerSettings', {
      gameId,
      turnTimerEnabled,
      turnTimerDuration
    });
  }, [turnTimerEnabled, turnTimerDuration, gameId, players, currentPlayerName]);

  // Broadcast opponent scores visibility changes to other players (host only)
  useEffect(() => {
    if (!socket || !socket.connected || !gameId) return;

    // Only the host should broadcast score visibility changes
    const player = players.find(p => p.name === currentPlayerName);
    if (!player || !player.isHost) return;

    socket.emit('updateScoreVisibilitySettings', {
      gameId,
      showOpponentScores
    });
  }, [showOpponentScores, gameId, players, currentPlayerName]);

  // Toggle ready status
  const toggleReady = () => {
    const newIsReady = !isReady;
    setIsReady(newIsReady);
    
    if (socket && socket.connected) {
      socket.safeEmit('playerReady', { gameId, playerName: currentPlayerName, isReady: newIsReady }, (err, response) => {
        if (err) {
          console.error('Failed to update ready status:', err.message);
          setError('Failed to update ready status: ' + err.message);
          // Revert the ready status if there was an error
          setIsReady(isReady);
          return;
        }
        
        // Handle success or error from server
        if (!response.success) {
          console.error('Server rejected ready status update:', response?.error || 'Unknown error');
          setError(response?.error || 'Failed to update ready status');
          // Revert the ready status if the server didn't accept it
          setIsReady(isReady);
        } else if (response.players) {
          // Update player list with latest data from server
          setPlayers(response.players);
        }
      });
    }
  };

  // Copy game ID to clipboard
  const copyGameId = () => {
    navigator.clipboard.writeText(gameId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy game ID:', err);
    });
  };

  // Share game via WhatsApp
  const shareToWhatsApp = () => {
    const gameUrl = `https://qwixx-online.nl/?join=${gameId}`;
    const message = t('lobby.whatsappMessage', { gameId, gameUrl });
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    openExternalUrl(whatsappUrl);
  };

  // Handle leaving the lobby
  const handleLeaveLobby = () => {
    // Set a flag to track if we've already executed the callback
    let callbackExecuted = false;
    
    // Function to execute the callback only once
    const executeCallback = () => {
      if (!callbackExecuted && onLeaveLobby) {
        callbackExecuted = true;
        onLeaveLobby();
      }
    };
    
    if (socket && socket.connected) {
      // Use safeEmit with a shorter timeout to notify the server
      socket.timeout(5000).emit('leaveGame', { gameId, playerName: currentPlayerName }, (err) => {
        if (err) {
          console.log('Server response error or timeout when leaving game:', err);
        }
        
        // Always execute callback even if there's an error response from server
        executeCallback();
      });
      
      // Set a backup timer to ensure we always leave even if the server doesn't respond
      setTimeout(() => {
        executeCallback();
      }, 1000);
    } else {
      // If socket isn't connected, just execute the callback
      executeCallback();
    }
  };

  // Check if the current player is the host
  const isHost = useCallback(() => {
    if (players.length === 0) return false;
    
    const player = players.find(p => p.name === currentPlayerName);
    return player && player.isHost;
  }, [players, currentPlayerName]);

  // Check if all players are ready
  const allPlayersReady = useCallback(() => {
    if (players.length === 0) return false;
    
    // First check if all expected players have joined (based on maxPlayers)
    if (players.length < maxPlayers) {
      return false;
    }
    
    // Then check if all players are ready (host must also be ready)
    return players.every(player => player.isReady);
  }, [players, maxPlayers]);

  // Start the game (host only)
  const handleStartGame = () => {
    console.log("Attempting to start game as host");
    console.log("Current players:", players);
    console.log(`Current player (${currentPlayerName}) is host:`, isHost());
    console.log("All players ready:", allPlayersReady());
    
    if (!isHost()) {
      setError("Only the host can start the game");
      return;
    }
    
    if (players.length < maxPlayers) {
      setError(`Waiting for more players to join (${players.length}/${maxPlayers})`);
      return;
    }
    
    if (!allPlayersReady()) {
      setError("All players must be ready to start the game");
      return;
    }
    
    if (socket && socket.connected) {
      console.log("Emitting startGame event for game:", gameId);
      socket.safeEmit('startGame', {
        gameId,
        turnTimerEnabled,
        turnTimerDuration: turnTimerEnabled ? turnTimerDuration : null,
        showOpponentScores
      }, (err, response) => {
        if (err) {
          console.error('Start game error:', err.message);
          setError('Failed to start game: ' + err.message);
          return;
        }
        
        // Updated to handle undefined responses
        if (response && response.success === false) {
          console.error('Server rejected game start:', response?.error || 'Unknown error');
          const errorString = typeof response.error === 'object' 
            ? (response.error?.message || JSON.stringify(response.error) || 'Unknown error') 
            : String(response.error || 'Unknown error');
          setError(errorString);
        } else {
          console.log("Game start success response:", response || "No detailed response");
          // The gameStart event will trigger the actual game start via the event listener
        }
      });
    } else {
      // Fallback for offline mode
      console.log("Falling back to offline mode, directly calling onStartGame");
      onStartGame();
    }
  };

  // Number of bots currently in the lobby and whether a slot is free
  const botCount = players.filter(p => p.isBot).length;
  const freeSlots = Math.max(0, maxPlayers - players.length);

  // Host adds an AI bot to fill an open slot
  const handleAddBot = () => {
    if (!isHost() || freeSlots <= 0) return;
    if (socket && socket.connected) {
      socket.safeEmit('addBot', { gameId }, (err, response) => {
        if (err) {
          setError('Failed to add bot: ' + err.message);
        } else if (response && response.success === false) {
          setError(response.error || 'Failed to add bot');
        }
      });
    }
  };

  // Host removes the most recently added bot
  const handleRemoveBot = () => {
    if (!isHost() || botCount <= 0) return;
    if (socket && socket.connected) {
      socket.safeEmit('removeBot', { gameId }, (err, response) => {
        if (err) {
          setError('Failed to remove bot: ' + err.message);
        } else if (response && response.success === false) {
          setError(response.error || 'Failed to remove bot');
        }
      });
    }
  };

  // Render the lobby UI
  return (
    <div className="lobby-page" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 'calc(100vh - 4rem)'
    }}>
      <div className="lobby-container" style={{
        backgroundColor: 'white', 
        borderRadius: '0.5rem', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
        padding: '1.5rem',
        maxWidth: '420px',
        margin: '2rem auto',
        position: 'relative',
        width: '100%'
      }}>
        {/* Add 3D Dice Header with Game Title */}
        <div className="dice-header" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          marginBottom: '0.5rem', 
          position: 'relative' 
        }}>
          {/* Language selector - positioned like in GamePage */}
          <div style={{
            position: 'absolute',
            left: '-25px',
            top: '-20px',
            transform: 'scale(0.625)',
            zIndex: 10
          }}>
            <LanguageSelector />
          </div>
          
          {/* Information button - positioned opposite to language selector */}
          <div style={{ 
            position: 'absolute',
            right: '-15px', 
            top: '-15px',
            zIndex: 10 
          }}>
            <button 
              className="info-button" 
              onClick={openRulesModal}
              aria-label={t('gameRules.title')}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.2s ease'
              }}
            >
              i
            </button>
          </div>
          
          {/* 3D Dice Container */}
          <div className="dice-container" style={{ perspective: '800px' }}>
            <div 
              className="die red"
              style={{ 
                transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transformStyle: 'preserve-3d'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateY(360deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateY(0deg)'}
            >
              <div className="die-face die-front one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
            
            <div 
              className="die yellow"
              style={{ 
                transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transformStyle: 'preserve-3d'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(360deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0deg)'}
            >
              <div className="die-face die-front two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
            
            <div 
              className="die green"
              style={{ 
                transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transformStyle: 'preserve-3d'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateZ(360deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateZ(0deg)'}
            >
              <div className="die-face die-front three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            </div>
            
            <div 
              className="die blue"
              style={{ 
                transition: 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                transformStyle: 'preserve-3d'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(180deg) rotateY(180deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg)'}
            >
              <div className="die-face die-front four">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-back three">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-top five">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-bottom two">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-left six">
                <div className="die-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <div className="die-face die-right one">
                <div className="die-dots">
                  <div className="dot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Game Title */}
        <h1 className="game-title" style={{ marginTop: '0', textAlign: 'center' }}>{t('gameTitle')}</h1>

        <div className="lobby-header" style={{ 
          textAlign: 'center', 
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}>
          <h1 style={{ 
            textAlign: 'center', 
            margin: '1rem 0',
            width: '100%',
            position: 'relative',
            zIndex: 1
          }}>{t('lobby.title')}</h1>
        </div>
        
        {showError && error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <div className="game-info" style={{
          width: '80%',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '0.75rem',
            textAlign: 'center',
            marginTop: '0'
          }}>
            {t('lobby.shareGameTitle')}
          </h3>
          <div className="game-id-container">
            <span>{t('lobby.gameId')}: <strong>{gameId}</strong></span>
            <button
              className={`copy-button ${copied ? 'copied' : ''}`}
              onClick={copyGameId}
            >
              {copied ? t('lobby.copied') : t('lobby.copy')}
            </button>
            <button
              className="whatsapp-button"
              onClick={shareToWhatsApp}
              title={t('lobby.shareWhatsApp')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
        </div>
        
          {connectionError && (
            <div className="connection-warning">
              <span role="img" aria-label="Warning">⚠️</span> {t('lobby.offlineMode')}
          </div>
          )}
        </div>

        {/* Add bots - Host only */}
        {isHost() && (
          <div className="bot-controls" style={{
            width: '80%',
            margin: '0.75rem auto 0',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.5rem',
              marginTop: '0'
            }}>
              {t('lobby.addBotsTitle')}
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 0.5rem' }}>
              {t('lobby.botSlots', { count: freeSlots })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button
                type="button"
                className="bot-button add-bot-button"
                onClick={handleAddBot}
                disabled={freeSlots <= 0}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: freeSlots <= 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: freeSlots <= 0 ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  opacity: freeSlots <= 0 ? 0.7 : 1
                }}
              >
                {t('lobby.addBot')}
              </button>
              <button
                type="button"
                className="bot-button remove-bot-button"
                onClick={handleRemoveBot}
                disabled={botCount <= 0}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: botCount <= 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: botCount <= 0 ? '#9ca3af' : '#ef4444',
                  color: '#fff',
                  opacity: botCount <= 0 ? 0.7 : 1
                }}
              >
                {t('lobby.removeBot')}
              </button>
            </div>
          </div>
        )}

        <div className="player-list" style={{
          width: '80%',
          margin: '0 auto'
        }}>
          <h2>{t('lobby.players')} ({players.length}/{maxPlayers})</h2>
          <ul>
            {players.map((player, index) => (
              <li key={index} className={`player-item ${player.isHost ? 'host' : ''} ${player.isReady ? 'ready' : ''} ${player.isBot ? 'bot' : ''}`}>
                <span className="player-name">
                  {player.name}
                  {player.isHost && <span className="host-badge">{t('lobby.host')}</span>}
                  {player.isBot && <span className="bot-badge">{t('lobby.botBadge')}</span>}
                    </span>
                {player.isReady && !player.isBot && (
                  <span className="ready-badge">{t('lobby.ready')}</span>
            )}
              </li>
            ))}
          </ul>
        </div>

        {/* Turn Timer Settings - Host Only */}
        {isHost() && (
          <div className="timer-settings" style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginTop: '1rem',
            width: '80%',
            margin: '1rem auto 0'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              textAlign: 'center',
              marginTop: '0'
            }}>
              {t('lobby.speedChallengeTitle')}
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: turnTimerEnabled ? '0.75rem' : '0'
            }}>
              <label style={{
                fontWeight: '500',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                ⏱️ {t('lobby.turnTimer')}
              </label>
              <button
                onClick={() => setTurnTimerEnabled(!turnTimerEnabled)}
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  border: 'none',
                  backgroundColor: turnTimerEnabled ? '#10b981' : '#9ca3af',
                  color: 'white',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                {turnTimerEnabled ? t('lobby.timerOn') : t('lobby.timerOff')}
              </button>
            </div>

            {turnTimerEnabled && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  {t('lobby.timerDuration')}:
                </span>
                <select
                  value={turnTimerDuration}
                  onChange={(e) => setTurnTimerDuration(Number(e.target.value))}
                  style={{
                    padding: '0.375rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value={15}>15 {t('lobby.seconds')}</option>
                  <option value={30}>30 {t('lobby.seconds')}</option>
                  <option value={45}>45 {t('lobby.seconds')}</option>
                  <option value={60}>60 {t('lobby.seconds')}</option>
                </select>
              </div>
            )}
          </div>
        )}

        {/* Show timer setting to non-host players (read-only) */}
        {!isHost() && turnTimerEnabled && (
          <div style={{
            backgroundColor: '#fef3c7',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginTop: '1rem',
            width: '80%',
            margin: '1rem auto 0',
            textAlign: 'center',
            color: '#92400e',
            fontSize: '0.875rem'
          }}>
            ⏱️ {t('lobby.timerEnabled', { seconds: turnTimerDuration })}
          </div>
        )}

        {/* Opponent Scores Visibility Settings - Host Only */}
        {isHost() && (
          <div className="opponent-scores-settings" style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginTop: '1rem',
            width: '80%',
            margin: '1rem auto 0'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '0.75rem',
              textAlign: 'center',
              marginTop: '0'
            }}>
              {t('lobby.opponentScoresTitle')}
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem'
            }}>
              <label style={{
                fontWeight: '500',
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                📊 {t('lobby.showOpponentScores')}
              </label>
              <button
                onClick={() => setShowOpponentScores(!showOpponentScores)}
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  border: 'none',
                  backgroundColor: showOpponentScores ? '#10b981' : '#9ca3af',
                  color: 'white',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                {showOpponentScores ? t('lobby.opponentScoresOn') : t('lobby.opponentScoresOff')}
              </button>
            </div>
            <p style={{
              textAlign: 'center',
              fontSize: '0.75rem',
              color: '#6b7280',
              marginTop: '0.5rem',
              marginBottom: '0'
            }}>
              {t('lobby.opponentScoresDescription')}
            </p>
          </div>
        )}

        {/* Show opponent scores setting to non-host players (read-only) */}
        {!isHost() && showOpponentScores && (
          <div style={{
            backgroundColor: '#dbeafe',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginTop: '1rem',
            width: '80%',
            margin: '1rem auto 0',
            textAlign: 'center',
            color: '#1e40af',
            fontSize: '0.875rem'
          }}>
            📊 {t('lobby.opponentScoresEnabled')}
          </div>
        )}

        <div className="lobby-actions" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
          width: '100%',
          marginTop: '1rem'
        }}>
          <button
            className={`ready-button ${isReady ? 'ready' : ''}`}
            onClick={toggleReady}
            style={{
              width: '100%',
              maxWidth: '280px',
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.375rem',
              backgroundColor: isReady ? '#10b981' : '#3b82f6',
              color: 'white',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
          >
            {isReady ? t('lobby.notReady') : t('lobby.ready')}
          </button>
          
          {isHost() && (
            <button 
              className={`start-button ${allPlayersReady() ? 'enabled' : 'disabled'}`}
              onClick={handleStartGame} 
              disabled={!allPlayersReady()}
              style={{
                width: '100%',
                maxWidth: '280px',
                padding: '0.75rem',
                border: 'none',
                borderRadius: '0.375rem',
                backgroundColor: allPlayersReady() ? '#f59e0b' : '#9ca3af',
                color: 'white',
                fontWeight: '500',
                cursor: allPlayersReady() ? 'pointer' : 'not-allowed',
                opacity: allPlayersReady() ? '1' : '0.7',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
            >
              {t('lobby.startGame')}
              {!allPlayersReady() && (
                <div style={{
                  position: 'absolute',
                  bottom: '-24px',
                  left: '0',
                  right: '0',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  color: '#6b7280'
                }}>
                  {players.length < maxPlayers 
                    ? t('lobby.waitingForPlayers') + ` (${players.length}/${maxPlayers})`
                    : t('lobby.waitingForReady')
                  }
                </div>
              )}
            </button>
          )}
          
          <button 
            className="leave-button"
            onClick={handleLeaveLobby}
            style={{
              width: '100%',
              maxWidth: '280px',
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.375rem',
              backgroundColor: '#ef4444',
              color: 'white',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {t('lobby.leave')}
          </button>
        </div>
        
        {/* Copyright notice */}
        <div style={{ textAlign: 'center', margin: '1.5rem 0 0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
          {t('common.copyright')}
        </div>
        
        {/* Add disclaimer */}
        <div className="disclaimer-container">
          <span className="disclaimer-trigger">{t('disclaimer.title')}</span>
          <div className="disclaimer-content">
            {t('disclaimer.content')}
          </div>
        </div>
        </div>
        
      {showNamePrompt && (
        <div className="name-prompt-overlay">
          <div className="name-prompt">
            <h2>{t('lobby.enterName')}</h2>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder={t('lobby.namePlaceholder')}
              maxLength={12}
            />
            {nameError && <div className="name-error">{nameError}</div>}
            <button onClick={handleSubmitName}>
              {t('lobby.continue')}
            </button>
          </div>
        </div>
      )}

      {/* Rules Modal */}
      {showRulesModal && (
        <div className="modal-overlay">
          <div className="rules-modal">
            <div className="rules-header">
              <h2 style={{ 
                textAlign: 'center', 
                marginBottom: '0.5rem',
                background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
                fontSize: '1.75rem',
                fontWeight: '700',
                position: 'relative',
                display: 'inline-block',
                padding: '0 1rem'
              }}>
                {t('gameRules.title')}
              </h2>
              <button 
                className="close-btn" 
                onClick={closeRulesModal}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  zIndex: 2,
                  padding: '5px 10px'
                }}
              >
                &times;
              </button>
            </div>
            <div className="rules-content">
              <p>{t('gameRules.overview')}</p>
              
              {/* Dynamically display all game rule sections */}
              {Array.from({ length: 9 }, (_, i) => (
                <React.Fragment key={i}>
                  <h3>{t(`gameRules.sections.${i}.title`)}</h3>
                  <p>{t(`gameRules.sections.${i}.content`)}</p>
                </React.Fragment>
              ))}

              {/* Donation section - Temporarily disabled (no brand license yet) */}
              {/* <div style={{
                marginTop: '2rem',
                textAlign: 'center',
                padding: '1rem',
                borderTop: '2px solid #e5e7eb'
              }}>
                <p style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  color: '#374151'
                }}>
                  {t('gameRules.donation')}
                </p>
                <img
                  src="/bmc_qr.png"
                  alt="Scan QR code to support Qwixx Online with a coffee donation"
                  style={{
                    maxWidth: '200px',
                    height: 'auto',
                    margin: '0 auto',
                    display: 'block'
                  }}
                />
              </div> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LobbyPage; 